/** The redesign's frame (owner picked option A, 4 Nov): sidebar grouped by job, a top bar with
 *  search + feed freshness, a tab bar on phones. Pages not yet redesigned render their classic
 *  version inside it, so nothing is ever missing from the new design. Styles: next.css, all
 *  scoped under `.nx` so the classic design is untouched. */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BadgePercent, BottleWine, ChartColumnDecreasing, CircleAlert, ClockAlert, ClockCheck, Database, Factory, Files,
  HandCoins, House, ListChecks, LogOut, Menu, Package, Scale, Settings, Ship, TrendingUp, Truck, User, Users,
  Warehouse, X, type LucideIcon,
} from "lucide-react";
// Every icon is the one docs/design/tokens.json names for that thing (nav-*, fresh, late, user...):
// one registry, so the app and the design boards always draw the same picture.

import { TASKS_CHANGED } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { Logo, useAuth } from "../Auth";
import { GlobalSearch, SwitchDesign, useTheme } from "../Layout";
import { FONT_THEMES, GOOGLE_FONTS } from "./fonts";
import "./next.css";

interface Item { to: string; label: string; icon: LucideIcon; badge?: number }
interface Freshness { scheduled: number; quiet: { code: string; name: string; summary: string }[] }

/** Is `to` (which may carry ?tab=) the page we are on? */
function isOn(to: string, path: string, search: string): boolean {
  const [p, q] = to.split("?");
  if (p === "/") return path === "/";
  if (q) return path === p && new URLSearchParams(search).toString().includes(q);
  if (path !== p && !path.startsWith(p + "/")) return false;
  // "/stock" is not on while a sibling entry with its own ?tab= is.
  return !(p === "/stock" && new URLSearchParams(search).get("tab") === "transit");
}

export function Shell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const overview = useApi<{ issues: { total: number }; documents: Record<string, number>; tasks_open?: number }>("/overview");
  const fresh = useApi<Freshness>("/freshness");
  const [theme, setTheme] = useTheme("theme-new", ["auto", "light", "dark", "shipwreck"]);
  const [open, setOpen] = useState(false);
  // A theme with its own typefaces (Shipwreck: Oswald + Lato) loads them only when chosen.
  useEffect(() => {
    if (!GOOGLE_FONTS || !FONT_THEMES.includes(theme) || theme === "light" || theme === "dark") return;
    if (document.querySelector(`link[data-nx-fonts]`)) return;
    const link = Object.assign(document.createElement("link"), { rel: "stylesheet", href: GOOGLE_FONTS });
    link.setAttribute("data-nx-fonts", "");
    document.head.appendChild(link);
  }, [theme]);
  const { pathname, search } = useLocation();
  useEffect(() => setOpen(false), [pathname, search]);
  const reload = overview.reload;
  useEffect(() => {
    window.addEventListener(TASKS_CHANGED, reload);
    return () => window.removeEventListener(TASKS_CHANGED, reload);
  }, [reload]);
  // Ctrl/⌘ K puts the cursor in search, as in the tools people already know.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        const box = document.querySelector<HTMLInputElement>(".nx-top input[type=search]");
        if (box) { e.preventDefault(); box.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const o = overview.data;
  const groups: { title?: string; items: Item[] }[] = [
    { items: [{ to: "/", label: "Home", icon: House }] },
    { title: "Stock", items: [
      { to: "/stock", label: "By warehouse", icon: Package },
      { to: "/stock?tab=transit", label: "In transit", icon: Truck },
      { to: "/shipments", label: "Shipments", icon: Ship },
      { to: "/production", label: "Production", icon: Factory },
    ] },
    { title: "Sales", items: [
      { to: "/sales", label: "Sales", icon: TrendingUp },
      { to: "/customers", label: "Customers", icon: Users },
      { to: "/depletions", label: "Depletions", icon: ChartColumnDecreasing },
    ] },
    { title: "Money", items: [
      ...(user?.sees_books ? [{ to: "/quickbooks", label: "Books (QuickBooks)", icon: Scale }] : []),
      { to: "/costs", label: "Costs & margin", icon: BadgePercent },
    ] },
    { title: "Paperwork", items: [
      { to: "/documents", label: "Documents", icon: Files, badge: o?.documents?.draft || undefined },
    ] },
    { title: "Catalogue", items: [
      { to: "/products", label: "Products", icon: BottleWine },
      { to: "/locations", label: "Locations", icon: Warehouse },
    ] },
    { title: "Data", items: [
      { to: "/data-quality", label: "Issues to review", icon: CircleAlert, badge: o?.issues.total || undefined },
      { to: "/sources", label: "Sources", icon: Database },
      { to: "/tasks", label: "Tasks", icon: ListChecks, badge: o?.tasks_open || undefined },
    ] },
  ];
  const tabs: Item[] = [
    { to: "/", label: "Home", icon: House },
    { to: "/stock", label: "Stock", icon: Package },
    { to: "/sales", label: "Sales", icon: TrendingUp },
    user?.sees_books ? { to: "/quickbooks", label: "Money", icon: HandCoins } : { to: "/costs", label: "Money", icon: HandCoins },
  ];

  return (
    <div className={`nx${open ? " nx-open" : ""}`}>
      <aside className="nx-side" aria-label="Main navigation">
        <div className="nx-brand">
          <Link to="/" aria-label="Home"><Logo className="nx-logo" /></Link>
          <button type="button" className="nx-icon-btn nx-close" aria-label="Close menu" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <nav>
          {groups.map((g, i) => g.items.length > 0 && (
            <div key={i} className="nx-group">
              {g.title && <p className="nx-group-title">{g.title}</p>}
              {g.items.map((it) => (
                <NavItem key={it.to} it={it} on={isOn(it.to, pathname, search)} />
              ))}
            </div>
          ))}
        </nav>
        <div className="nx-side-foot">
          <NavItem it={{ to: "/settings", label: "Settings", icon: Settings }} on={isOn("/settings", pathname, search)} />
          <SwitchDesign to="classic" className="nx-btn nx-btn-wide nx-side-switch" />
        </div>
      </aside>
      <div className="nx-main">
        <header className="nx-top">
          <button type="button" className="nx-icon-btn nx-menu" aria-label="Open menu" onClick={() => setOpen(true)}><Menu size={22} /></button>
          <GlobalSearch placeholder="Search products, customers, documents…  (Ctrl K)" />
          <FreshPill f={fresh.data} />
          <AccountMenu theme={theme} setTheme={setTheme} />
        </header>
        <main className="nx-content">{children}</main>
      </div>
      {open && <button type="button" className="nx-scrim" aria-label="Close menu" onClick={() => setOpen(false)} />}
      <nav className="nx-tabs" aria-label="Main">
        {tabs.map((t) => (
          <Link key={t.label} to={t.to} className={`nx-tab${isOn(t.to, pathname, search) ? " is-on" : ""}`}
                aria-current={isOn(t.to, pathname, search) ? "page" : undefined}>
            <t.icon size={22} aria-hidden="true" /><span>{t.label}</span>
          </Link>
        ))}
        <button type="button" className="nx-tab" onClick={() => setOpen(true)}><Menu size={22} aria-hidden="true" /><span>More</span></button>
      </nav>
    </div>
  );
}

function NavItem({ it, on }: { it: Item; on: boolean }) {
  return (
    <Link to={it.to} className={`nx-nav${on ? " is-on" : ""}`} aria-current={on ? "page" : undefined}>
      <it.icon size={18} aria-hidden="true" />
      <span>{it.label}</span>
      {it.badge ? <span className="nx-badge" aria-label={`${it.badge} open`}>{it.badge}</span> : null}
    </Link>
  );
}

/** "All feeds fresh" / "1 feed late: Planet Florida". Links to Sources. Icon + words, never colour alone. */
function FreshPill({ f }: { f: Freshness | null }) {
  if (!f) return null;
  const late = f.quiet.length;
  const text = late === 0 ? `All ${f.scheduled} feeds fresh`
    : late === 1 ? `1 feed late: ${f.quiet[0].name}` : `${late} feeds late`;
  return (
    <Link to="/sources" className={`nx-pill ${late ? "is-late" : "is-fresh"}`} aria-label={text} title={f.quiet.map((q) => q.summary).join("\n") || undefined}>
      {late ? <ClockAlert size={16} aria-hidden="true" /> : <ClockCheck size={16} aria-hidden="true" />}
      <span className="nx-pill-text">{text}</span>
      <span className="nx-pill-short" aria-hidden="true">{late ? `${late} late` : "Fresh"}</span>
    </Link>
  );
}

/** Who is signed in, their theme, the way back to the classic design and signing out: always in
 *  the top bar, never at the bottom of a long sidebar where a laptop screen cuts it off. */
function AccountMenu({ theme, setTheme }: { theme: string; setTheme: (t: string) => void }) {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  if (!user) return null;
  return (
    <div className="nx-account" ref={box}>
      <button type="button" className="nx-account-btn" aria-expanded={open} aria-haspopup="true"
              aria-label={`Account: ${user.name}`} onClick={() => setOpen((o) => !o)}>
        <span className="nx-avatar" aria-hidden="true">{user.name.slice(0, 1).toUpperCase()}</span>
        <span className="nx-account-name">{user.name}</span>
      </button>
      {open && (
        <div className="nx-account-menu" role="group" aria-label="Account">
          <div className="nx-account-who"><strong>{user.name}</strong><span>{user.email}</span></div>
          <Link to="/settings?tab=account" className="nx-menu-row"><User size={18} aria-hidden="true" />Your account</Link>
          <label className="nx-menu-row nx-theme">
            <span>Theme</span>
            <select value={theme} onChange={(e) => setTheme(e.target.value)}>
              <option value="auto">System</option><option value="light">Light</option>
              <option value="dark">Dark</option><option value="shipwreck">Shipwreck</option>
            </select>
          </label>
          <SwitchDesign to="classic" className="nx-btn nx-btn-wide" />
          <button type="button" className="nx-menu-row" onClick={() => void signOut()}><LogOut size={18} aria-hidden="true" />Sign out</button>
        </div>
      )}
    </div>
  );
}
